import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Users,
  Search,
  RefreshCcw,
  CheckCircle2,
  Trash2,
  LogOut,
  ChevronDown,
  ChevronUp,
  Filter,
  Eye,
  FileText,
  MessageSquareText,
  TrendingUp,
  FolderPlus,
  Image as ImageIcon,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const aspxSelect = (v) => {
  const s = (v ?? "").toString().trim();
  return s === "" ? "Select" : s;
};

const EmployeeApprovalList = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = Number(localStorage.getItem("finYearId") || 0);
  const userId = Number(localStorage.getItem("userId") || 1);

  const [showFilters, setShowFilters] = useState(true);
  const screenBadge = useMemo(() => "INFRA SCREEN : EMP APPROVAL", []);

  // dropdowns
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [grades, setGrades] = useState([]);

  // filters
  const [branchId, setBranchId] = useState(() => {
    const saved = (localStorage.getItem("branchId") || "").toString().trim();
    return saved && saved !== "Select" ? saved : "";
  });
  const [deptId, setDeptId] = useState("");
  const [desigId, setDesigId] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [pf, setPf] = useState("");
  const [paymode, setPaymode] = useState("");
  const [empName, setEmpName] = useState("");
  const [empCode, setEmpCode] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // selection (approve/delete)
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // resign selection (separate from selectedIds)
  const [resignSelectedIds, setResignSelectedIds] = useState([]);
  const [resignSelectAll, setResignSelectAll] = useState(false);

  // per-row resign date (like ASP grid)
  const [resignDates, setResignDates] = useState({}); // { [employeeId]: "yyyy-mm-dd" }

  // bulk resign (top control)
  const [bulkResignDate, setBulkResignDate] = useState("");

  const [didInitialSearch, setDidInitialSearch] = useState(false);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const okBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (selectedIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (selectedIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700");

  const warnBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (resignSelectedIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-amber-600 hover:bg-amber-700");

  const ensureFinYear = () => {
    if (!finYearId) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    return true;
  };

  const getRowEmployeeId = (r) =>
    Number(r?.fld_employeeid || r?.Employeeid || r?.employeeId || r?.fld_Employeeid || 0);

  const getRowEmpCode = (r) =>
    String(r?.fld_employeecode || r?.Employeecode || r?.empCode || r?.fld_Employeecode || "").trim();

  const getRowEmpName = (r) => String(r?.fld_Employee || r?.Employee || r?.employee || "").trim();

  const getRowPhoto = (r) =>
    String(r?.fld_photo || r?.Photo || r?.fld_empphoto || r?.fld_Photo || "").trim();

  const fetchListWithBranchOverride = async (branchOverride) => {
    if (!ensureFinYear()) return;

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/employee-approval/list`, {
        params: {
          finYearId,
          branchId: aspxSelect(branchOverride ?? branchId),
          desigId: aspxSelect(desigId),
          deptId: aspxSelect(deptId),
          gradeId: aspxSelect(gradeId),
          pf: aspxSelect(pf),
          paymode: aspxSelect(paymode),
          empName: empName.trim(),
          empCode: empCode.trim(),
          fromDate: fromDate || "",
          toDate: toDate || "",
        },
      });

      if (res?.data?.status) {
        const list = res.data.data || [];
        setRows(list);

        // reset selections on new search
        setSelectedIds([]);
        setSelectAll(false);

        setResignSelectedIds([]);
        setResignSelectAll(false);
        setResignDates({});

        toast.success(`Loaded ${list.length} record(s)`);
      } else {
        toast.error(res?.data?.message || "Failed to load list");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "List load failed");
    } finally {
      setLoading(false);
    }
  };

  const loadDropdowns = async () => {
    if (!ensureFinYear()) return;
    try {
      setLoading(true);
      const [b, d, de, g] = await Promise.all([
        axios.get(`${BASE_URL}/api/employee-approval/branches`, { params: { finYearId } }),
        axios.get(`${BASE_URL}/api/employee-approval/departments`, { params: { finYearId } }),
        axios.get(`${BASE_URL}/api/employee-approval/designations`, { params: { finYearId } }),
        axios.get(`${BASE_URL}/api/employee-approval/grades`, { params: { finYearId } }),
      ]);

      setBranches(b?.data?.data || []);
      setDepartments(d?.data?.data || []);
      setDesignations(de?.data?.data || []);
      setGrades(g?.data?.data || []);

      // default search by login branch id (once)
      if (!didInitialSearch) {
        const loginBranch = (localStorage.getItem("branchId") || "").toString().trim();
        const effectiveBranch = loginBranch && loginBranch !== "Select" ? loginBranch : branchId;

        if (effectiveBranch && effectiveBranch !== branchId) setBranchId(effectiveBranch);
        setDidInitialSearch(true);

        await fetchListWithBranchOverride(effectiveBranch);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Dropdown load failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async () => {
    await fetchListWithBranchOverride(branchId);
  };

  useEffect(() => {
    loadDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSelect = (employeeId) => {
    setSelectedIds((prev) =>
      prev.includes(employeeId) ? prev.filter((x) => x !== employeeId) : [...prev, employeeId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    const ids = rows.map((r) => getRowEmployeeId(r)).filter(Boolean);
    setSelectedIds(ids);
    setSelectAll(true);
  };

  const toggleResignSelect = (employeeId) => {
    setResignSelectedIds((prev) =>
      prev.includes(employeeId) ? prev.filter((x) => x !== employeeId) : [...prev, employeeId]
    );
  };

  const handleResignSelectAll = () => {
    if (resignSelectAll) {
      setResignSelectedIds([]);
      setResignSelectAll(false);
      return;
    }
    const ids = rows.map((r) => getRowEmployeeId(r)).filter(Boolean);
    setResignSelectedIds(ids);
    setResignSelectAll(true);
  };

  const bulkApprove = async () => {
    if (!ensureFinYear()) return;
    if (selectedIds.length === 0) return toast.warn("Select at least one employee");

    if (!window.confirm(`Bulk approve ${selectedIds.length} employee(s)?`)) return;

    try {
      setLoading(true);
      const res = await axios.post(`${BASE_URL}/api/employee-approval/bulk/approve`, {
        finYearId,
        userId,
        employeeIds: selectedIds,
      });

      if (res?.data?.status) {
        toast.success(res?.data?.message || "Bulk Approved");
        await fetchList();
      } else {
        toast.error(res?.data?.message || "Bulk approve failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Bulk approve failed");
    } finally {
      setLoading(false);
    }
  };

  const bulkResign = async () => {
    if (!ensureFinYear()) return;
    if (resignSelectedIds.length === 0) return toast.warn("Select at least one employee to resign");
    if (!bulkResignDate) {
      // allow bulk date empty only if every selected row has a row-date
      const allHaveRowDate = resignSelectedIds.every((id) => !!resignDates[id]);
      if (!allHaveRowDate) return toast.warn("Please select Bulk Resign Date (or fill per-row resign dates).");
    }

    if (!window.confirm(`Bulk resign approve ${resignSelectedIds.length} employee(s)?`)) return;

    try {
      setLoading(true);

      const items = resignSelectedIds.map((id) => ({
        employeeId: id,
        resignDate: resignDates[id] || bulkResignDate,
      }));

      // final validation
      const bad = items.find((x) => !x.resignDate);
      if (bad) return toast.warn("Missing resign date for one or more employees.");

      const res = await axios.post(`${BASE_URL}/api/employee-approval/bulk/resign`, {
        finYearId,
        userId,
        items,
      });

      if (res?.data?.status) {
        toast.success(res?.data?.message || "Resign Approved");
        await fetchList();
      } else {
        toast.error(res?.data?.message || "Bulk resign failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Bulk resign failed");
    } finally {
      setLoading(false);
    }
  };

  const bulkDelete = async () => {
    if (!ensureFinYear()) return;
    if (selectedIds.length === 0) return toast.warn("Select at least one employee");

    if (!window.confirm(`Delete ${selectedIds.length} employee(s)? This cannot be undone.`)) return;

    try {
      setLoading(true);

      const selectedRows = rows.filter((r) => selectedIds.includes(getRowEmployeeId(r)));
      const items = selectedRows.map((r) => ({
        employeeId: getRowEmployeeId(r),
        empCode: getRowEmpCode(r),
      }));

      const res = await axios.post(`${BASE_URL}/api/employee-approval/bulk/delete`, {
        finYearId,
        userId,
        items,
      });

      if (res?.data?.status) {
        toast.success(res?.data?.message || "Deleted");
        await fetchList();
      } else {
        toast.error(res?.data?.message || "Bulk delete failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Bulk delete failed");
    } finally {
      setLoading(false);
    }
  };

  const onClickEmpCode = (r) => {
    const employeeId = getRowEmployeeId(r);
    if (!employeeId) return toast.warn("employeeId not found in row");
    navigate(`/PayRoll/EmployeeApprovalManagement/${employeeId}`);
  };

  // ASP-like links (we can change routes later)
  const onClickPFForms = (r) => {
    const employeeId = getRowEmployeeId(r);
    if (!employeeId) return toast.warn("employeeId not found");
    navigate(`/PayRoll/EmployeePFForms/${employeeId}`);
  };

  const onClickRemarks = (r) => {
    const employeeId = getRowEmployeeId(r);
    if (!employeeId) return toast.warn("employeeId not found");
    navigate(`/PayRoll/EmployeeRemarks/${employeeId}`);
  };

  const onClickPerformance = (r) => {
    const employeeId = getRowEmployeeId(r);
    if (!employeeId) return toast.warn("employeeId not found");
    navigate(`/PayRoll/EmployeePerformance/${employeeId}`);
  };

  const onClickFileManager = (r) => {
    const employeeId = getRowEmployeeId(r);
    if (!employeeId) return toast.warn("employeeId not found");
    navigate(`/PayRoll/EmployeeFileManager/${employeeId}`);
  };

  const PhotoCell = ({ row }) => {
    const filename = getRowPhoto(row);
    const employeeId = getRowEmployeeId(row);

    if (!filename) {
      return (
        <div className="flex items-center justify-center gap-2 opacity-70">
          <ImageIcon className="w-4 h-4" />
          <span className="text-xs">No Photo</span>
        </div>
      );
    }

    return (
      <button
        type="button"
        onClick={() => navigate(`/payroll/EmployeeApprovalManagement/${employeeId}`)}
        title="Open details"
        className="inline-flex items-center justify-center"
      >
        <img
          src={`${BASE_URL}/employee_photo/${filename}`}
          alt="Emp"
          className="w-12 h-12 object-cover rounded-xl border"
        />
      </button>
    );
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2600}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE APPROVAL MANAGEMENT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* top header */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Users className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Employee Approval List
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Click Employee Code to open details
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {rows.length} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={loadDropdowns} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Reload Masters
              </button>

              <button onClick={fetchList} className={`${primaryBtn}`} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* filters */}
          {showFilters && (
            <div className={cardBase + " p-6 mb-6"}>
              <div className="flex items-center gap-2 mb-4">
                <Filter className={isDark ? "text-gray-200" : "text-gray-700"} />
                <div className="text-lg font-extrabold">Search Filters</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className={labelBase}>Branch</label>
                  <select
                    className={inputBase}
                    value={branchId}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBranchId(v);
                      localStorage.setItem("branchId", String(v || ""));
                    }}
                  >
                    <option value="">Select</option>
                    {branches.map((b, idx) => (
                      <option key={idx} value={b?.fld_Branchid ?? b?.branchId ?? b?.BranchId ?? ""}>
                        {b?.fld_BranchName ?? b?.branchName ?? b?.BranchName ?? "Branch"}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Department</label>
                  <select className={inputBase} value={deptId} onChange={(e) => setDeptId(e.target.value)}>
                    <option value="">Select</option>
                    {departments.map((d) => (
                      <option key={d.deptId} value={d.deptId}>
                        {d.deptName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Designation</label>
                  <select className={inputBase} value={desigId} onChange={(e) => setDesigId(e.target.value)}>
                    <option value="">Select</option>
                    {designations.map((d) => (
                      <option key={d.desigId} value={d.desigId}>
                        {d.desigName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Grade</label>
                  <select className={inputBase} value={gradeId} onChange={(e) => setGradeId(e.target.value)}>
                    <option value="">Select</option>
                    {grades.map((g) => (
                      <option key={g.gradeId} value={g.gradeId}>
                        {g.gradeName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>PF</label>
                  <select className={inputBase} value={pf} onChange={(e) => setPf(e.target.value)}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Honorarium">Honorarium</option>
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Paymode</label>
                  <select className={inputBase} value={paymode} onChange={(e) => setPaymode(e.target.value)}>
                    <option value="">Select</option>
                    <option value="Cash">Cash</option>
                    <option value="Bank">Bank</option>
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Emp Name</label>
                  <input
                    className={inputBase}
                    value={empName}
                    onChange={(e) => setEmpName(e.target.value)}
                    placeholder="Employee Name"
                  />
                </div>

                <div>
                  <label className={labelBase}>Emp Code</label>
                  <input
                    className={inputBase}
                    value={empCode}
                    onChange={(e) => setEmpCode(e.target.value)}
                    placeholder="Employee Code"
                  />
                </div>

                <div>
                  <label className={labelBase}>From Date</label>
                  <input type="date" className={inputBase} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </div>

                <div>
                  <label className={labelBase}>To Date</label>
                  <input type="date" className={inputBase} value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* bulk actions */}
          <div className={cardBase + " p-6 mb-6"}>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                <div
                  className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                    isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-800"
                  }`}
                >
                  Selected: {selectedIds.length}
                </div>

                <button onClick={bulkApprove} className={okBtn} disabled={loading || selectedIds.length === 0}>
                  <CheckCircle2 className="w-4 h-4" />
                  Bulk Approve
                </button>

                <button onClick={bulkDelete} className={dangerBtn} disabled={loading || selectedIds.length === 0}>
                  <Trash2 className="w-4 h-4" />
                  Bulk Delete
                </button>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
                <div className="min-w-[220px]">
                  <label className={labelBase}>Bulk Resign Date</label>
                  <input
                    type="date"
                    className={inputBase}
                    value={bulkResignDate}
                    onChange={(e) => setBulkResignDate(e.target.value)}
                  />
                </div>

                <div
                  className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                    isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-800"
                  }`}
                >
                  Resign Selected: {resignSelectedIds.length}
                </div>

                <button onClick={bulkResign} className={warnBtn} disabled={loading || resignSelectedIds.length === 0}>
                  <LogOut className="w-4 h-4" />
                  Bulk Resign Approve
                </button>
              </div>
            </div>
          </div>

          {/* table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">BranchName</th>

                    {/* NEW ASP-like columns */}
                    <th className="px-4 py-4 text-center font-bold">Photo</th>
                    {/* <th className="px-4 py-4 text-center font-bold">PF Forms</th> */}
                    <th className="px-4 py-4 text-center font-bold">Remarks</th>
                    <th className="px-4 py-4 text-center font-bold">Performance</th>
                    <th className="px-4 py-4 text-center font-bold">File Manager</th>

                    {/* <th className="px-4 py-4 text-center font-bold">Quick</th> */}

                    {/* delete selection (existing selectedIds) */}
                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Delete
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                      </div>
                    </th>

                    {/* resign columns */}
                    <th className="px-4 py-4 text-center font-bold">Resign Date</th>
                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Resign
                        <input type="checkbox" checked={resignSelectAll} onChange={handleResignSelectAll} />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 14 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={14} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const employeeId = getRowEmployeeId(r);
                      const code = getRowEmpCode(r);
                      const name = getRowEmpName(r) || "-";
                      const desig = r?.fld_designationname ?? r?.desigName ?? r?.DesignationName ?? "-";
                      const branchName = r?.fld_branchname ?? r?.branchname ?? r?.BranchName ?? "-";

                      return (
                        <tr
                          key={employeeId || idx}
                          className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {idx + 1}
                          </td>

                          <td className="px-4 py-4 text-sm text-center">
                            <button
                              onClick={() => onClickEmpCode(r)}
                              className={`font-extrabold underline underline-offset-4 ${
                                isDark ? "text-sky-300 hover:text-sky-200" : "text-blue-700 hover:text-blue-900"
                              }`}
                              title="Open Employee Approval Details"
                            >
                              {code || "OPEN"}
                            </button>
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {name}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {desig}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {branchName}
                          </td>

                          {/* Photo */}
                          <td className="px-4 py-4 text-center">
                            <PhotoCell row={r} />
                          </td>

                          {/* PF Forms */}
                          {/* <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => onClickPFForms(r)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-2xl text-sm font-bold ${
                                isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                              }`}
                            >
                              <FileText className="w-4 h-4" />
                              Add PF
                            </button>
                          </td> */}

                          {/* Remarks */}
                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => onClickRemarks(r)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-2xl text-sm font-bold ${
                                isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                              }`}
                            >
                              <MessageSquareText className="w-4 h-4" />
                              Remarks
                            </button>
                          </td>

                          {/* Performance */}
                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => onClickPerformance(r)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-2xl text-sm font-bold ${
                                isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                              }`}
                            >
                              <TrendingUp className="w-4 h-4" />
                              Performance
                            </button>
                          </td>

                          {/* File Manager */}
                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => onClickFileManager(r)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-2xl text-sm font-bold ${
                                isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                              }`}
                            >
                              <FolderPlus className="w-4 h-4" />
                              Add
                            </button>
                          </td>

                          {/* Quick */}
                          {/* <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => onClickEmpCode(r)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-2xl hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all shadow"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                          </td> */}

                          {/* Delete checkbox (re-using selectedIds like your bulk delete/approve) */}
                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(employeeId)}
                              onChange={() => toggleSelect(employeeId)}
                              className="w-4 h-4"
                            />
                          </td>

                          {/* Resign date per row */}
                          <td className="px-4 py-4 text-center">
                            <input
                              type="date"
                              className={`px-3 py-2 rounded-xl border text-sm ${
                                isDark ? "bg-gray-800/40 border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"
                              }`}
                              value={resignDates[employeeId] || ""}
                              onChange={(e) =>
                                setResignDates((prev) => ({
                                  ...prev,
                                  [employeeId]: e.target.value,
                                }))
                              }
                            />
                          </td>

                          {/* Resign checkbox (separate selection for resign flow) */}
                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={resignSelectedIds.includes(employeeId)}
                              onChange={() => toggleResignSelect(employeeId)}
                              className="w-4 h-4"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 text-sm opacity-70">
            Tip: Click <b>Employee Code</b> to open clean approval details page.
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeApprovalList;
